"use client";

import { ArrowRight, ChevronDown } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TheMainStage() {
  const word1 = useRevealOnScroll<HTMLSpanElement>({
    threshold: 0.1
  });
  const word2 = useRevealOnScroll<HTMLSpanElement>({
    threshold: 0.1
  });
  const word3 = useRevealOnScroll<HTMLSpanElement>({
    threshold: 0.1
  });
  const eyebrow = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const paragraph = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.1
  });
  const actions = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const image = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  const credit = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  const scrollIndicator = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  return <section data-section-id="4022" id="the-main-stage" className="relative bg-background text-foreground overflow-hidden min-h-screen w-full">
      <div className="relative mx-auto max-w-[1600px] px-6 sm:px-8 lg:px-12 pt-24 pb-16 lg:pt-32 lg:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-start">
          {}
          <div className="lg:col-span-5 lg:pt-8 order-2 lg:order-1 flex flex-col">
            {}
            <div ref={eyebrow.ref} className={`mb-10 lg:mb-16 transition-all duration-600 ease-out ${eyebrow.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              <span className="font-mono text-xs tracking-[0.2em] uppercase text-primary">
                SECTION_01 // THE_GARAGE_ROOM
              </span>
            </div>

            {}
            <h1 className="font-default font-extrabold leading-[0.9] tracking-tighter text-foreground text-5xl sm:text-6xl md:text-7xl lg:text-7xl xl:text-8xl 2xl:text-9xl uppercase">
              <span ref={word1.ref} className={`block transition-all duration-600 ease-out ${word1.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
                Loud{" "}
                <span className="font-extrabold text-primary">
                  /
                </span>
              </span>
              <span ref={word2.ref} className={`block transition-all duration-600 ease-out delay-100 ${word2.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
                Raw{" "}
                <span className="font-extrabold text-primary">
                  /
                </span>
              </span>
              <span ref={word3.ref} className={`block transition-all duration-600 ease-out delay-200 ${word3.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
                Live.
              </span>
            </h1>

            {}
            <p ref={paragraph.ref} className={`mt-10 max-w-md font-default text-lg lg:text-xl text-foreground/80 leading-relaxed transition-all duration-600 ease-out delay-300 ${paragraph.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              A community-driven sanctuary for local bands. From tube amps to 
              premium kits, we provide the grit and the gear to track your next 
              masterpiece.
            </p>

            {}
            <div ref={actions.ref} className={`mt-10 flex flex-wrap items-center gap-6 transition-all duration-600 ease-out delay-400 ${actions.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              <Button asChild size="lg" className="rounded-md px-8 h-12 bg-primary text-primary-foreground hover:translate-x-1 hover:translate-y-1 transition-transform duration-0 font-default font-bold text-sm tracking-wide shadow-md">
                <Link to="/BookPage">Check Availability</Link>
              </Button>
              <Link to="/TheGearPage" className="group inline-flex items-center gap-2 font-mono text-sm text-foreground hover:text-primary transition-colors duration-0">
                <span className="border-b-2 border-primary/30 group-hover:border-primary pb-1 uppercase">
                  The Arsenal
                </span>
                <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>

          {}
          <div className="lg:col-span-7 order-1 lg:order-2 relative">
            <div ref={image.ref} className={`relative rounded-md overflow-hidden shadow-2xl transition-all duration-1000 ease-out ${image.isVisible ? "opacity-100 scale-100" : "opacity-0 scale-[0.98]"}`} style={{
            aspectRatio: "4 / 5"
          }}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/gritty_studio_tracking_session.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/gritty_studio_tracking_session.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/gritty_studio_tracking_session.png-wvc-sizes" alt="Local band tracking live in a gritty studio environment" className="w-full h-full object-cover grayscale contrast-125" loading="eager" decoding="async" />
              {}
              <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent pointer-events-none" />
              <div className="absolute inset-0 opacity-10 pointer-events-none bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1693037/img/stardust.png')]" />
            </div>

            {}
            <div ref={credit.ref} className={`absolute -bottom-6 -left-4 sm:left-6 lg:-left-12 lg:-bottom-8 max-w-xs bg-card text-card-foreground rounded-md border border-border shadow-lg p-5 sm:p-6 transition-all duration-1000 ease-out delay-500 ${credit.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-default font-bold text-lg sm:text-xl text-foreground leading-tight uppercase">
                    Live Room A
                  </p>
                  <p className="mt-2 font-mono text-[10px] sm:text-xs tracking-[0.18em] uppercase text-muted-foreground">
                    Tracking: The Void — 2024
                  </p>
                </div>
                <div className="size-3 rounded-md mt-2 shrink-0" style={{
                backgroundColor: "var(--primary)"
              }} />
              </div>
            </div>
          </div>
        </div>

        {}
        <div ref={scrollIndicator.ref} className={`hidden lg:flex absolute bottom-8 left-12 flex-col items-center gap-3 transition-all duration-1000 ease-out delay-1000 ${scrollIndicator.isVisible ? "opacity-100" : "opacity-0"}`}>
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground" style={{
          writingMode: "vertical-rl"
        }}>
            Scroll
          </span>
          <div className="relative h-16 w-px bg-border overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-6 bg-primary" style={{
            animation: "scrollLine 2s ease-in-out infinite"
          }} />
          </div>
          <ChevronDown className="size-3 text-primary" style={{
          animation: "scrollBounce 2s ease-in-out infinite"
        }} />
        </div>
      </div>

      <style>{`
        @keyframes scrollLine {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(400%); }
        }
        @keyframes scrollBounce {
          0%, 100% { transform: translateY(0); opacity: 0.5; }
          50% { transform: translateY(4px); opacity: 1; }
        }
      `}</style>
    </section>;
}